<script setup lang="ts">
    import { reactive, ref, onMounted } from 'vue';
    import { PendingDeviceSummary, getPendingDevicesForUser, approveDevice } from './UserPendingDevices.access';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import List from '@shared/components/List/List.vue';

    const props = defineProps<{
        userId: string;
    }>();

    const state = reactive<{
        pending: PendingDeviceSummary[];
        loading: boolean;
    }>({
        pending: [],
        loading: true,
    });

    const approving = ref<string | null>(null);

    onMounted(async () => {
        await load();
    });

    async function load() {
        state.loading = true;
        state.pending = await getPendingDevicesForUser(props.userId);
        state.loading = false;
    }

    function deviceType(userAgent: string): string {
        try {
            const parsed = JSON.parse(userAgent);
            return parsed.deviceType ?? userAgent;
        } catch {
            return userAgent;
        }
    }

    async function approve(trustedDeviceId: string) {
        approving.value = trustedDeviceId;
        try {
            await approveDevice(trustedDeviceId);
            await load();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Could not approve device', content: 'The approval failed. Please refresh and try again.' });
        } finally {
            approving.value = null;
        }
    }
</script>

<script lang="ts">
    export default { name: 'UserPendingDevices' };
</script>

<template>
    <h4>Pending device verifications</h4>

    <List
        itemId="TrustedDeviceId"
        displayAs="table"
        :items="state.pending"
        :loading="state.loading"
        :tableHeaders="['Device type', 'Last sign-in attempt', 'Code expires', '']"
        hideSearch>
        <template #toolbar>
            <button class="btn btn-default" @click="load">Refresh</button>
        </template>
        <template #item="{ item }">
            <td>{{ deviceType(item.UserAgent) }}</td>
            <td>{{ formatDateTime(item.CreatedAt) }}</td>
            <td>{{ item.CodeExpiresAt ? formatDateTime(item.CodeExpiresAt) : '—' }}</td>
            <td class="autofit">
                <button
                    class="btn btn-success btn-xs"
                    :class="{ 'is-busy': approving === item.TrustedDeviceId }"
                    :disabled="approving === item.TrustedDeviceId"
                    @click="approve(item.TrustedDeviceId)">
                    Approve device
                </button>
            </td>
        </template>
    </List>
</template>
